import React, { useContext, useState, useEffect, useRef } from 'react';
import {
  auth,
} from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  updatePassword,
} from 'firebase/auth';

const AuthContext = React.createContext();
const API_URL = process.env.REACT_APP_API_URL;

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isSigningUp = useRef(false);

  async function signup(email, password) {
    isSigningUp.current = true;
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const actionCodeSettings = {
        url: `${window.location.origin}/verify`,
        handleCodeInApp: true,
      };
      await sendEmailVerification(userCredential.user, actionCodeSettings);
      await signOut(auth);
    } finally {
      isSigningUp.current = false;
      setCurrentUser(null);
    }
  }

  async function login(email, password) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    if (!user.emailVerified) {
      await signOut(auth);
      const error = new Error('Email not verified');
      error.code = 'auth/email-not-verified';
      throw error;
    }

    const isJustVerified = sessionStorage.getItem('just_verified');
    if (isJustVerified) {
      try {
        const token = await user.getIdToken();
        await fetch(`${API_URL}/api/user/verified`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        sessionStorage.removeItem('just_verified');
      } catch (error) {
        console.error('Failed to update verification status on backend:', error);
      }
    }

    return userCredential;
  }

  function loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  }

  function logout() {
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      if (isSigningUp.current) {
        return;
      }
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    loginWithGoogle,
    logout,
    updatePassword,
    auth,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
